import type { Product } from './types';

export const PRODUCT_ORDER: Product[] = [
  'express',
  'regional',
  'suburban',
  'subway',
  'tram',
  'bus',
  'ferry',
  'other',
];

export const PRODUCT_LABEL: Record<Product, string> = {
  express: 'Fernverkehr',
  regional: 'Regional',
  suburban: 'S-Bahn',
  subway: 'U-Bahn',
  tram: 'Tram',
  bus: 'Bus',
  ferry: 'Fähre',
  other: 'Sonstige',
};

export const PRODUCT_COLOR: Record<Product, string> = {
  express: '#e2372b',
  regional: '#c8452f',
  suburban: '#12933f',
  subway: '#1f56c3',
  tram: '#d4322c',
  bus: '#8e44c9',
  ferry: '#0aa2c0',
  other: '#5b6472',
};

const timeFormat = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });
const numberFormat = new Intl.NumberFormat('de-DE');

export const formatCount = (n: number): string => numberFormat.format(n);

export function formatClock(value: string | number | null): string {
  if (value === null) return '';
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? timeFormat.format(ms) : '';
}

/** "+3 min", "pünktlich", "-1 min" — the wording used across the whole UI. */
export function formatDelay(seconds: number | null): string {
  if (seconds === null) return 'keine Prognose';
  const minutes = Math.round(seconds / 60);
  if (minutes === 0) return 'pünktlich';
  return `${minutes > 0 ? '+' : '−'}${Math.abs(minutes)} min`;
}

export function delayClass(seconds: number | null): string {
  if (seconds === null) return '';
  if (seconds >= 180) return 'delay-pos';
  if (seconds <= -60) return 'delay-neg';
  return 'delay-ok';
}

export function formatAgo(timestamp: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 5) return 'gerade eben';
  if (seconds < 60) return `vor ${seconds}s`;
  const minutes = Math.round(seconds / 60);
  return `vor ${minutes} min`;
}

/**
 * Delay colour ramp used when the map is coloured by punctuality instead of
 * by line: green (on time) through amber to red, plus grey for "no prognosis".
 */
export function delayColor(seconds: number | null): string {
  if (seconds === null) return '#7d8798';
  if (seconds <= -60) return '#4aa3ff';
  if (seconds < 120) return '#3ddc97';
  if (seconds < 300) return '#ffd028';
  if (seconds < 600) return '#ff9a2e';
  return '#ff4d4d';
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Station names in German feeds carry a lot of noise; trim the worst of it. */
export function cleanStopName(name: string): string {
  return name.replace(/\s*\((Berlin|Brandenburg|Potsdam)\)\s*$/i, '').trim();
}
